import {i18n} from '@yourcoach/i18n';

import type {CourseApiModel} from '../../../api/course';
import type {MembershipExpandedCommon} from '../../../api/membership';
import type {ProgramApiModel} from '../../../api/program';
import type {AsyncStrategy} from '../utils';

import InfoSVG from '../../../assets/icons/primary/Info Circle.svg';
import SuccessIcon from '../../../assets/icons/primary/Tick Square.svg';
import {alert} from '../../../components/alert';
import {isIamCoach} from '../../../utils/entity/isIamCoach';
import {AppError} from '../../../utils/errors/app';
import {logger} from '../../../utils/logger';
import {isDefined} from '../../../utils/typecheck';
import {COMMAND} from '../utils';
import {AcceptRequestedMembershipStrategyFactory} from './membershipAcceptRequested/AcceptRequestedMembershipStrategyFactory';

type Config = {
  memberships: MembershipExpandedCommon[];
  target?: CourseApiModel | ProgramApiModel;
};

export {membershipAcceptRequested};

const strategy: AsyncStrategy<Config> = async (roles, config) => {
  if (isIamCoach()) {
    return roles.coach(config);
  } else {
    return roles.client(config);
  }
};

async function membershipAcceptRequested(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

membershipAcceptRequested.asCoach = asCoach;
membershipAcceptRequested.asClient = asClient;

async function asClient(_config: Config) {
  throw new AppError('not_allowed');
}

async function asCoach({memberships, target}: Config) {
  const isConfirmed = await alert.confirm({
    icon: InfoSVG,
    iconColor: 'icon3',
    isDismissible: true,
  });

  if (!isConfirmed) {
    return COMMAND.CANCEL;
  }

  const isInviteAction = isDefined(target);

  try {
    const acceptStrategy = AcceptRequestedMembershipStrategyFactory.create({
      memberships,
      target,
    });

    await acceptStrategy.execute();

    await alert.show({
      buttons: [
        {
          label: i18n.t('shared:great_excl'),
          scheme: 'primary',
        },
      ],
      icon: SuccessIcon,
      iconColor: 'icon4',
      title: isInviteAction
        ? i18n.t('shared:invitation_success', {
            count: memberships.length,
          })
        : i18n.t('shared:clients_added_to_program'),
    });
  } catch (error) {
    logger.error(error);

    throw error;
  }
}
